import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { kycPortalService } from '../../services/kycPortalService';
import { getFormSections, buildEmptyFormData, formCompleteness, normalizeFormData } from '../KYC/kycFormSchema';
import {
    EXTRACTION_STATUS_UNREADABLE,
    analysisOutcome,
    readingSummary,
} from '../KYC/kycDocumentReading';
import KYCPortalShell from './KYCPortalShell';
import KYCPortalDeclaration from './KYCPortalDeclaration';
import ReadingProgress from '../ui/ReadingProgress';

/** Small badge shown next to AI-pre-filled fields (ClickUp Step 2: verify). */
const AIBadge = ({ source }) => (
    <span
        className="kyc-portal-ai-badge"
        title={source ? `Extracted by IMT AI from: ${source} — please verify` : 'Extracted by IMT AI — please verify'}
    >
        AI-filled — verify
    </span>
);

/** What one uploaded file did on the last run, in the partner's words.
 *  Deliberately does not repeat the reader's own reason: that sentence is
 *  written for the reviewer and can ask for a better scan, which is the last
 *  thing to say to a partner whose copy was fine and ours was the side that
 *  fell over. The reviewer sees it on the case instead.
 *
 *  `nothingRead` is the run's own verdict that not one word came out of any
 *  file. It settles the rows this screen cannot settle on its own: a file with
 *  no reading result of its own was still in a run that read nothing, so it was
 *  not read either. Every other unrecorded file gets wording that says only
 *  what the form shows — no field came from it — and never implies we read it. */
const documentRow = (state, fieldCount, nothingRead) => {
    if (state === 'unreadable' || (state === 'unknown' && nothingRead)) {
        return { tone: 'err', label: 'Couldn’t be read' };
    }
    if (state === 'not_analysed') return { tone: 'warn', label: 'Not read yet' };
    if (state === 'blank') return { tone: 'warn', label: 'No text in this file' };
    if (fieldCount > 0) {
        return { tone: 'ok', label: `${fieldCount} field${fieldCount === 1 ? '' : 's'} filled in` };
    }
    if (state === 'read') return { tone: 'warn', label: 'Read — nothing the form needed' };
    return { tone: 'warn', label: 'No details taken from this file' };
};

/** Post-analysis feedback: per-document outcome + overall summary / failure. */
const AIResultsPanel = ({ caseData }) => {
    const aiInfo = caseData.ai_info || {};
    const failed = caseData.extraction_status === 'failed';
    const docs = caseData.documents || [];

    if (failed) {
        return (
            <div className="kyc-portal-ai-results is-failed">
                <div className="kyc-portal-ai-results-head">
                    <span className="kyc-portal-ai-results-icon is-err" aria-hidden="true" />
                    <div>
                        <strong>Analysis couldn’t be completed</strong>
                        <p>
                            Your documents are saved, but we couldn’t finish analysing them just
                            now. Please click “Analyze documents with IMT AI” to try again — if it
                            keeps happening, contact your IMT representative.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (!aiInfo.last_run && caseData.extraction_status !== EXTRACTION_STATUS_UNREADABLE) return null;

    // Fields the AI pulled per source document (from extraction provenance).
    const perSource = {};
    Object.values(aiInfo.provenance || {}).forEach((info) => {
        const src = info && info.source;
        if (src) perSource[src] = (perSource[src] || 0) + 1;
    });

    const totalFields = (aiInfo.filled_fields || []).length;
    const openItems = (aiInfo.questions || []).length;
    // ai_info carries the run's per-file read records; without it this panel is
    // back to guessing from a field count.
    const outcome = analysisOutcome({
        documents: docs,
        aiPayload: aiInfo,
        extractionStatus: caseData.extraction_status,
        filledFieldCount: totalFields,
    });

    // The tick is only ever drawn for a run that pre-filled something. Every
    // other outcome — including an end state this screen doesn't know — falls
    // through to wording that claims nothing, never to the success panel.
    let headline;
    let explanation;
    if (outcome.kind === 'ok') {
        headline = `Analysis complete — ${totalFields} field${totalFields === 1 ? '' : 's'} pre-filled`;
        explanation = openItems === 0
            ? 'All required information was found. Review it in Step 2, then submit in Step 3.'
            : `${openItems} item${openItems === 1 ? '' : 's'} still need your input — see “Open items from IMT AI” in Step 3.`;
    } else if (outcome.kind === 'nothing_matched') {
        headline = 'Your documents were read — nothing to pre-fill';
        explanation = 'We read them but didn’t find any of the details this form asks for. If you '
            + 'think a file should contain them, check it’s the right document and analyze again — '
            + 'otherwise please fill in Step 2 yourself.';
    } else if (outcome.kind === 'unread') {
        headline = 'We couldn’t read your documents';
        // Deliberately does not promise anyone will read them by hand: nothing
        // notifies HMS when this happens yet. Put that sentence back the day
        // somebody is told.
        explanation = 'Your files arrived safely — we just couldn’t get the text out of them, '
            + 'and that is ours to sort out, not yours. They are saved on your case. Please '
            + 'fill in Step 2 yourself and carry on; there is no need to upload anything again.';
    } else {
        headline = 'Analysis finished — IMT is checking the result';
        explanation = 'Your documents are with IMT. Please fill in Step 2 yourself and carry on; '
            + 'there is nothing more you need to do here.';
    }

    const panelTone = { ok: '', unread: ' is-failed' }[outcome.kind] ?? ' is-warn';
    const iconTone = { ok: 'is-ok', unread: 'is-err' }[outcome.kind] ?? 'is-warn';
    const unreadable = outcome.unreadableCount;

    return (
        <div className={`kyc-portal-ai-results${panelTone}`}>
            <div className="kyc-portal-ai-results-head">
                <span className={`kyc-portal-ai-results-icon ${iconTone}`} aria-hidden="true" />
                <div>
                    <strong>{headline}</strong>
                    <p>{explanation}</p>
                </div>
            </div>
            <ul className="kyc-portal-ai-results-docs">
                {docs.map((doc) => {
                    const { state } = readingSummary(doc, outcome.index);
                    const { tone, label } = documentRow(
                        state, perSource[doc.original_filename] || 0, outcome.kind === 'unread',
                    );
                    return (
                        <li key={doc.id} className={`is-${tone}`}>
                            <span className="kyc-portal-ai-doc-status" aria-hidden="true" />
                            <span className="kyc-portal-ai-doc-name">{doc.original_filename}</span>
                            <span className="kyc-portal-ai-doc-count">{label}</span>
                        </li>
                    );
                })}
            </ul>
            {outcome.kind !== 'unread' && unreadable > 0 && (
                <p className="kyc-portal-ai-results-hint">
                    We couldn’t read {unreadable === 1 ? 'one of your files' : `${unreadable} of your files`} —
                    that is ours to sort out, not yours. IMT can see which ones and will read them
                    by hand, so there is no need to upload them again.
                </p>
            )}
        </div>
    );
};

const STEPS = [
    { key: 'documents', title: 'Upload Documents', sub: 'KYC document checklist' },
    { key: 'form', title: 'Onboarding Form', sub: 'Company, ownership & banking' },
    { key: 'declaration', title: 'Sanctions Declaration', sub: 'Read & sign electronically' },
    { key: 'review', title: 'Review & Submit', sub: 'Final check before sending to IMT' },
];

/* ---------------------------------------------------------------- Step 1 */

const DocumentsStep = ({ caseData, onCaseUpdate, readOnly, onRunExtraction, analyzing, analysisError }) => {
    const [error, setError] = useState('');
    const [busyType, setBusyType] = useState(null);
    const fileInputRef = useRef(null);
    const pendingTypeRef = useRef(null);

    const startUpload = (docType) => {
        pendingTypeRef.current = docType;
        fileInputRef.current?.click();
    };

    const handleFileChosen = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        const docType = pendingTypeRef.current;
        if (!file || !docType) return;
        setError('');
        setBusyType(docType);
        try {
            const updated = await kycPortalService.uploadDocument(docType, file);
            onCaseUpdate(updated);
        } catch (err) {
            setError(err.response?.data?.error || 'Upload failed. Please try again.');
        } finally {
            setBusyType(null);
        }
    };

    const handleDelete = async (documentId) => {
        setError('');
        try {
            const updated = await kycPortalService.deleteDocument(documentId);
            onCaseUpdate(updated);
        } catch (err) {
            setError(err.response?.data?.error || 'Could not remove the document.');
        }
    };

    const documentsByType = {};
    (caseData.documents || []).forEach((doc) => {
        documentsByType[doc.doc_type] = documentsByType[doc.doc_type] || [];
        documentsByType[doc.doc_type].push(doc);
    });

    const requirementLabel = {
        mandatory: 'Mandatory',
        required: 'Required',
        optional: 'Optional',
        if_available: 'If available',
        if_prompted: 'Only if requested',
    };

    return (
        <div className="kyc-portal-card">
            <h2>KYC Document Checklist</h2>
            <p className="kyc-portal-help">
                Please upload a copy of each document <strong>in English</strong>.
                Accepted formats: PDF, Word, Excel, PNG or JPG (max. 15 MB per file).
                After uploading, IMT AI reads your documents and pre-fills the
                onboarding form for you to verify in Step 2.
            </p>
            {error && <div className="kyc-portal-error">{error}</div>}
            {analysisError && <div className="kyc-portal-error" role="alert">{analysisError}</div>}
            {!readOnly && (caseData.documents || []).length > 0 && (
                <div className="kyc-portal-ai-bar">
                    {analyzing ? (
                        <div className="kyc-portal-ai-progress">
                            <span className="kyc-portal-ai-status">
                                <span className="kyc-portal-spinner" aria-hidden="true" />
                                IMT AI is reading your {(caseData.documents || []).length} document
                                {(caseData.documents || []).length === 1 ? '' : 's'}… the form in Step 2
                                will fill in automatically.
                            </span>
                            <ReadingProgress
                                progress={caseData.reading_progress}
                                leaveText="You can carry on with Step 2 or leave this page — the reading continues."
                            />
                        </div>
                    ) : (
                        <>
                            <button type="button" className="kyc-portal-btn-secondary" onClick={onRunExtraction}>
                                Analyze documents with IMT AI
                            </button>
                            {caseData.ai_info?.last_run && (
                                <span className="kyc-portal-ai-status">
                                    Last analysis: {new Date(caseData.ai_info.last_run).toLocaleString()}
                                </span>
                            )}
                        </>
                    )}
                </div>
            )}
            {!analyzing && (caseData.documents || []).length > 0
                && (caseData.ai_info?.last_run
                    || caseData.extraction_status === 'failed'
                    || caseData.extraction_status === EXTRACTION_STATUS_UNREADABLE) && (
                <AIResultsPanel caseData={caseData} />
            )}
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                onChange={handleFileChosen}
            />
            {(caseData.document_checklist || []).map((item) => (
                <div className="kyc-portal-doc-item" key={item.code}>
                    <div style={{ flex: 1 }}>
                        <span className="kyc-portal-doc-label">{item.label}</span>
                        <span className={`kyc-portal-doc-req ${item.requirement}`}>
                            {requirementLabel[item.requirement]}
                        </span>
                        <div>
                            {(documentsByType[item.code] || []).map((doc) => (
                                <span className="kyc-portal-doc-file-chip" key={doc.id}>
                                    {doc.original_filename}
                                    {!readOnly && (
                                        <button
                                            type="button"
                                            title="Remove"
                                            onClick={() => handleDelete(doc.id)}
                                        >
                                            ×
                                        </button>
                                    )}
                                </span>
                            ))}
                        </div>
                    </div>
                    {!readOnly && (
                        <button
                            type="button"
                            className="kyc-portal-upload-btn"
                            disabled={busyType !== null}
                            onClick={() => startUpload(item.code)}
                        >
                            {busyType === item.code ? 'Uploading…' : '+ Upload'}
                        </button>
                    )}
                </div>
            ))}
        </div>
    );
};

/* ---------------------------------------------------------------- Step 2 */

/** Sticky in-form clickthrough for the long onboarding form: jump to a section,
 *  highlights the section you're currently viewing, dots sections that have data. */
const SectionNav = ({ sections, activeKey, onJump, isFilled }) => {
    const chipRefs = useRef({});
    useEffect(() => {
        const chip = chipRefs.current[activeKey];
        if (chip) chip.scrollIntoView({ block: 'nearest', inline: 'center' });
    }, [activeKey]);

    return (
        <nav className="kyc-portal-secnav" aria-label="Onboarding form sections">
            {sections.map((section, index) => (
                <button
                    key={section.key}
                    type="button"
                    ref={(el) => { chipRefs.current[section.key] = el; }}
                    className={
                        'kyc-portal-secnav-chip'
                        + (section.key === activeKey ? ' is-active' : '')
                        + (isFilled(section.key) ? ' is-filled' : '')
                    }
                    onClick={() => onJump(section.key)}
                    title={section.title}
                >
                    <span className="kyc-portal-secnav-num">{index + 1}</span>
                    <span className="kyc-portal-secnav-label">{section.title}</span>
                </button>
            ))}
        </nav>
    );
};

/**
 * A tickbox group rendering one of the CRM classification vocabularies.
 *
 * Deliberately plain checkboxes rather than the CRM tab's dropdown-with-chips:
 * the partner sees every option HMS can actually store, with no hidden list to
 * open. The stored value is the same list either way.
 */
const MultiSelectField = ({ options, selected, disabled, name, unmatched, onToggle }) => {
    const chosen = Array.isArray(selected) ? selected : [];
    return (
        <div className="kyc-portal-multiselect">
            {unmatched && unmatched.length > 0 && (
                <p className="kyc-portal-multiselect-legacy">
                    Previously entered as text and not matched to an IMT category:{' '}
                    <strong>{unmatched.join(', ')}</strong>. Please tick the closest option(s) below.
                </p>
            )}
            <div className="kyc-portal-multiselect-options">
                {options.map((option) => {
                    const id = `${name}-${option.replace(/[^a-zA-Z0-9]+/g, '-')}`;
                    return (
                        <label className="kyc-portal-multiselect-option" key={option} htmlFor={id}>
                            <input
                                id={id}
                                type="checkbox"
                                checked={chosen.includes(option)}
                                disabled={disabled}
                                onChange={() => onToggle(option)}
                            />
                            <span>{option}</span>
                        </label>
                    );
                })}
            </div>
        </div>
    );
};

const FormStep = ({ formData, setFormData, readOnly, sections, aiInfo, saveState, onBack, onContinue, legacyValues = [] }) => {
    const filledFields = aiInfo?.filled_fields || [];
    const provenance = aiInfo?.provenance || {};
    const isAiFilled = (path) => filledFields.includes(path);
    const sectionHasAi = (sectionKey) => filledFields.some(
        (path) => path === sectionKey || path.startsWith(`${sectionKey}.`),
    );
    const sourceFor = (path) => provenance[path]?.source || provenance[Object.keys(provenance).find((p) => p.startsWith(path)) || '']?.source;

    // True if a section holds any data (drives the green dot in the nav).
    const isSectionFilled = (sectionKey) => {
        const val = formData[sectionKey];
        if (!val) return false;
        if (Array.isArray(val)) return val.some((row) => Object.values(row || {}).some(Boolean));
        if (typeof val === 'object') {
            return Object.values(val).some((v) => (
                v && typeof v === 'object' ? Object.values(v).some(Boolean) : Boolean(v)
            ));
        }
        return Boolean(val);
    };

    // Tabbed view: only ONE section is shown at a time. Clicking a chip swaps
    // the card and scrolls back to the top of the form (no long scroll).
    const formTopRef = useRef(null);
    const [activeSection, setActiveSection] = useState(sections[0]?.key);

    // If the section set changes (STANDARD -> SA) and the active key disappears,
    // fall back to the first section.
    useEffect(() => {
        if (!sections.some((s) => s.key === activeSection)) {
            setActiveSection(sections[0]?.key);
        }
    }, [sections, activeSection]);

    const goToSection = (key) => {
        setActiveSection(key);
        if (formTopRef.current) {
            formTopRef.current.scrollIntoView({ block: 'start' });
        }
    };

    const setField = (sectionKey, fieldKey, value) => {
        setFormData((previous) => ({
            ...previous,
            [sectionKey]: { ...(previous[sectionKey] || {}), [fieldKey]: value },
        }));
    };

    // Tickbox groups store a LIST. Toggling is add/remove on that list — the
    // same shape the CRM partner profile keeps, so the value can cross the
    // seam into the profile untouched.
    const toggleMultiSelect = (sectionKey, fieldKey, option) => {
        setFormData((previous) => {
            const current = previous[sectionKey]?.[fieldKey];
            const list = Array.isArray(current) ? current : [];
            const next = list.includes(option)
                ? list.filter((item) => item !== option)
                : [...list, option];
            return {
                ...previous,
                [sectionKey]: { ...(previous[sectionKey] || {}), [fieldKey]: next },
            };
        });
    };

    const unmatchedFor = (sectionKey, fieldKey) => (
        legacyValues.find((l) => l.section === sectionKey && l.field === fieldKey)?.unmatched
    );

    const setTableCell = (sectionKey, rowIndex, columnKey, value) => {
        setFormData((previous) => {
            const rows = [...(previous[sectionKey] || [])];
            rows[rowIndex] = { ...(rows[rowIndex] || {}), [columnKey]: value };
            return { ...previous, [sectionKey]: rows };
        });
    };

    const addTableRow = (sectionKey) => {
        setFormData((previous) => ({
            ...previous,
            [sectionKey]: [...(previous[sectionKey] || []), {}],
        }));
    };

    const removeTableRow = (sectionKey, rowIndex) => {
        setFormData((previous) => ({
            ...previous,
            [sectionKey]: (previous[sectionKey] || []).filter((_, index) => index !== rowIndex),
        }));
    };

    const setFixedCell = (sectionKey, rowKey, columnKey, value) => {
        setFormData((previous) => ({
            ...previous,
            [sectionKey]: {
                ...(previous[sectionKey] || {}),
                [rowKey]: { ...((previous[sectionKey] || {})[rowKey] || {}), [columnKey]: value },
            },
        }));
    };

    const setExtraField = (sectionKey, fieldKey, value) => {
        setFormData((previous) => ({
            ...previous,
            [`${sectionKey}__extra`]: {
                ...(previous[`${sectionKey}__extra`] || {}),
                [fieldKey]: value,
            },
        }));
    };

    return (
        <div ref={formTopRef}>
            {sections.length > 1 && (
                <SectionNav
                    sections={sections}
                    activeKey={activeSection}
                    onJump={goToSection}
                    isFilled={isSectionFilled}
                />
            )}
            {sections.filter((section) => section.key === activeSection).map((section) => (
                <div
                    className="kyc-portal-card kyc-portal-form-section"
                    key={section.key}
                >
                    <h3>
                        {section.title}
                        {(section.type !== 'fields') && sectionHasAi(section.key) && (
                            <AIBadge source={sourceFor(section.key)} />
                        )}
                    </h3>
                    {section.help && <p className="kyc-portal-help">{section.help}</p>}

                    {section.type === 'fields' && (section.fields || []).map((field) => (
                        <div className="kyc-portal-field" key={field.key}>
                            <label>
                                {field.label}
                                {field.required && <span className="kyc-portal-required"> *</span>}
                                {isAiFilled(`${section.key}.${field.key}`) && (
                                    <AIBadge source={sourceFor(`${section.key}.${field.key}`)} />
                                )}
                            </label>
                            {field.help && <p className="kyc-portal-field-help">{field.help}</p>}
                            {field.type === 'multiselect' ? (
                                <MultiSelectField
                                    options={field.options}
                                    selected={formData[section.key]?.[field.key]}
                                    disabled={readOnly}
                                    name={`${section.key}-${field.key}`}
                                    unmatched={unmatchedFor(section.key, field.key)}
                                    onToggle={(option) => toggleMultiSelect(section.key, field.key, option)}
                                />
                            ) : field.type === 'yesno' ? (
                                <select
                                    value={formData[section.key]?.[field.key] || ''}
                                    onChange={(event) => setField(section.key, field.key, event.target.value)}
                                    disabled={readOnly}
                                >
                                    <option value="">— Select —</option>
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                </select>
                            ) : field.multiline ? (
                                <textarea
                                    rows={2}
                                    value={formData[section.key]?.[field.key] || ''}
                                    onChange={(event) => setField(section.key, field.key, event.target.value)}
                                    disabled={readOnly}
                                />
                            ) : (
                                <input
                                    type="text"
                                    value={formData[section.key]?.[field.key] || ''}
                                    onChange={(event) => setField(section.key, field.key, event.target.value)}
                                    disabled={readOnly}
                                />
                            )}
                        </div>
                    ))}

                    {section.type === 'table' && (
                        <>
                            <table className="kyc-portal-table">
                                <thead>
                                    <tr>
                                        {section.columns.map((column) => (
                                            <th key={column.key}>{column.label}</th>
                                        ))}
                                        {!readOnly && <th style={{ width: 36 }} />}
                                    </tr>
                                </thead>
                                <tbody>
                                    {(formData[section.key] || []).map((row, rowIndex) => (
                                        <tr key={rowIndex}>
                                            {section.columns.map((column) => (
                                                <td key={column.key}>
                                                    <input
                                                        type="text"
                                                        value={row?.[column.key] || ''}
                                                        onChange={(event) =>
                                                            setTableCell(section.key, rowIndex, column.key, event.target.value)
                                                        }
                                                        disabled={readOnly}
                                                    />
                                                </td>
                                            ))}
                                            {!readOnly && (
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="kyc-portal-row-remove"
                                                        title="Remove row"
                                                        onClick={() => removeTableRow(section.key, rowIndex)}
                                                    >
                                                        ×
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {!readOnly && (formData[section.key] || []).length < section.maxRows && (
                                <button
                                    type="button"
                                    className="kyc-portal-add-row"
                                    onClick={() => addTableRow(section.key)}
                                >
                                    + Add row
                                </button>
                            )}
                            {(section.extraFields || []).map((field) => (
                                <div className="kyc-portal-field" key={field.key} style={{ marginTop: '0.9rem' }}>
                                    <label>{field.label}</label>
                                    {field.type === 'yesno' ? (
                                        <select
                                            value={formData[`${section.key}__extra`]?.[field.key] || ''}
                                            onChange={(event) => setExtraField(section.key, field.key, event.target.value)}
                                            disabled={readOnly}
                                        >
                                            <option value="">— Select —</option>
                                            <option value="Yes">Yes</option>
                                            <option value="No">No</option>
                                        </select>
                                    ) : (
                                        <input
                                            type="text"
                                            value={formData[`${section.key}__extra`]?.[field.key] || ''}
                                            onChange={(event) => setExtraField(section.key, field.key, event.target.value)}
                                            disabled={readOnly}
                                        />
                                    )}
                                    {field.help && <p className="kyc-portal-help" style={{ margin: '0.35rem 0 0 0' }}>{field.help}</p>}
                                </div>
                            ))}
                        </>
                    )}

                    {section.type === 'fixed-table' && (
                        <table className="kyc-portal-table">
                            <thead>
                                <tr>
                                    <th>Function</th>
                                    {section.columns.map((column) => (
                                        <th key={column.key}>{column.label}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {section.rows.map((row) => (
                                    <tr key={row.key}>
                                        <td style={{ fontWeight: 600, fontSize: '0.85rem', paddingTop: '0.85rem' }}>
                                            {row.label}
                                        </td>
                                        {section.columns.map((column) => (
                                            <td key={column.key}>
                                                <input
                                                    type="text"
                                                    value={formData[section.key]?.[row.key]?.[column.key] || ''}
                                                    onChange={(event) =>
                                                        setFixedCell(section.key, row.key, column.key, event.target.value)
                                                    }
                                                    disabled={readOnly}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            ))}
            {sections.length > 1 && (() => {
                const activeIndex = sections.findIndex((s) => s.key === activeSection);
                const isFirst = activeIndex <= 0;
                const isLast = activeIndex >= sections.length - 1;
                return (
                    <div className="kyc-portal-secpager">
                        <button
                            type="button"
                            className="kyc-portal-btn-secondary"
                            onClick={() => (isFirst ? onBack?.() : goToSection(sections[activeIndex - 1].key))}
                        >
                            {isFirst ? '← Back to Documents' : '← Previous'}
                        </button>
                        <span className="kyc-portal-secpager-count">
                            Section {activeIndex + 1} of {sections.length}
                            {!readOnly && saveState ? ` · ${saveState}` : ''}
                        </span>
                        <button
                            type="button"
                            className={isLast ? 'kyc-portal-btn-primary' : 'kyc-portal-btn-secondary'}
                            onClick={() => (isLast ? onContinue?.() : goToSection(sections[activeIndex + 1].key))}
                        >
                            {isLast ? 'Continue to Review →' : 'Next →'}
                        </button>
                    </div>
                );
            })()}
        </div>
    );
};

/* ---------------------------------------------------------------- Step 3 */

const ReviewStep = ({ caseData, formData, onSubmit, submitting, submitError }) => {
    const checklist = caseData.document_checklist || [];
    const mandatoryMissing = checklist.filter(
        (item) => item.requirement === 'mandatory' && item.uploaded_count === 0,
    );
    const requiredMissing = checklist.filter(
        (item) => item.requirement === 'required' && item.uploaded_count === 0,
    );
    const { required, filled } = formCompleteness(formData, caseData.form_type);
    const aiQuestions = caseData.ai_info?.questions || [];
    const declarationSigned = ['signed', 'signed_manual'].includes(
        caseData.sanctions_declaration?.status,
    );

    return (
        <div className="kyc-portal-card">
            <h2>Review & Submit</h2>
            <p className="kyc-portal-help">
                Once submitted, your information goes to the IMT compliance team and can no
                longer be edited (IMT can reopen it for you if changes are needed).
            </p>

            <h3>Documents</h3>
            {mandatoryMissing.length > 0 ? (
                <div className="kyc-portal-error">
                    Mandatory documents missing: {mandatoryMissing.map((item) => item.label).join(', ')}
                </div>
            ) : (
                <div className="kyc-portal-success">All mandatory documents uploaded.</div>
            )}
            {requiredMissing.length > 0 && (
                <p className="kyc-portal-help">
                    Still outstanding ({requiredMissing.length}):{' '}
                    {requiredMissing.map((item) => item.label).join('; ')}.
                    You can submit with the mandatory minimum, but IMT may request these later.
                </p>
            )}

            <h3>Onboarding Form</h3>
            <p className="kyc-portal-help">
                {filled} of {required} key fields completed
                {filled < required ? ' — please check the form before submitting.' : '.'}
            </p>

            <h3>Sanctions Declaration</h3>
            {declarationSigned ? (
                <div className="kyc-portal-success">Signed — thank you.</div>
            ) : (
                <div className="kyc-portal-error">
                    The Counterparty Sanctions Declaration has not been signed yet — please
                    complete step 3 before submitting.
                </div>
            )}

            {aiQuestions.length > 0 && (
                <>
                    <h3>Open items from IMT AI</h3>
                    <p className="kyc-portal-help">
                        Our AI checked your pack and still needs the following:
                    </p>
                    <ul className="kyc-portal-ai-questions">
                        {aiQuestions.map((question, index) => (
                            <li key={index}>{question}</li>
                        ))}
                    </ul>
                </>
            )}

            {submitError && <div className="kyc-portal-error">{submitError}</div>}

            <div className="kyc-portal-gdpr">
                <h4>Personal Data Protection (GDPR)</h4>
                <p>
                    The information collected through this form is processed by IMT
                    International Minerals Trading AG, as the data controller, for the
                    purposes of customer relationship management, regulatory obligations, as
                    well as the setup and monitoring of credit facilities. This data is
                    intended for IMT&apos;s internal
                    departments and may be shared, where necessary, with our financial
                    partners and organizations involved in the implementation of credit
                    facilities. The data is retained only for the period strictly necessary
                    to fulfill the purposes of the processing and in accordance with
                    applicable legal obligations.
                </p>
                <p>
                    In accordance with Regulation (EU) 2016/679 (GDPR), you have the right
                    to access, rectify, erase, restrict, object to, and request the
                    portability of your personal data. You may exercise these rights by
                    contacting your IMT representative.
                    {/* TODO(Legal): IMT's data-protection contact address is to be
                        confirmed by Legal. This used to send GDPR requests to a
                        mailbox at HMS; do not put an address here until Legal
                        names IMT's own. */}
                </p>
                <p>
                    By submitting this form, you acknowledge that you have read this
                    information and consent to the processing of your personal data.
                </p>
            </div>

            <button
                type="button"
                className="kyc-portal-btn-success"
                disabled={submitting || mandatoryMissing.length > 0 || !declarationSigned}
                onClick={onSubmit}
            >
                {submitting ? 'Submitting…' : 'Submit to IMT'}
            </button>
        </div>
    );
};

/* ----------------------------------------------------------------- Main */

const KYCPortalOnboarding = () => {
    const history = useHistory();
    const [caseData, setCaseData] = useState(null);
    const [formData, setFormData] = useState(buildEmptyFormData());
    // Free-text classification answers from before the tickbox change that we
    // could not map to an HMS value — shown so the partner re-picks them.
    const [legacyValues, setLegacyValues] = useState([]);
    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [saveState, setSaveState] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const saveTimerRef = useRef(null);
    const dirtyRef = useRef(false);

    useEffect(() => {
        if (!kycPortalService.hasSession()) {
            history.replace('/kyc-portal/login');
            return;
        }
        const run = async () => {
            try {
                const data = await kycPortalService.getCase();
                setCaseData(data);
                const empty = buildEmptyFormData(data.form_type);
                // Partner Classification became tickboxes in Aug 2026; a case
                // answered before that holds strings. Coerce on load so the
                // partner sees their answer ticked, and keep whatever we could
                // not place so it is asked again rather than lost.
                const { formData: normalized, legacy } = normalizeFormData(
                    { ...empty, ...(data.form_data || {}) }, data.form_type,
                );
                setFormData(normalized);
                setLegacyValues(legacy);
            } catch (err) {
                if (err.response?.status !== 401) {
                    setLoadError('Could not load your KYC case. Please try again.');
                }
            } finally {
                setLoading(false);
            }
        };
        run();
    }, [history]);

    const isEditable = caseData?.is_editable;
    const analyzing = caseData?.extraction_status === 'queued' || caseData?.extraction_status === 'running';

    // While the AI pipeline runs, poll until it finishes and fold the
    // AI-filled values into the local form (never overwriting local edits).
    useEffect(() => {
        if (!analyzing) return undefined;
        const interval = setInterval(async () => {
            try {
                const data = await kycPortalService.getCase();
                setCaseData(data);
                if (data.extraction_status === 'done' && !dirtyRef.current) {
                    const empty = buildEmptyFormData(data.form_type);
                    const { formData: normalized } = normalizeFormData(
                        { ...empty, ...(data.form_data || {}) }, data.form_type,
                    );
                    setFormData(normalized);
                }
            } catch (err) {
                // transient — keep polling until status changes
            }
        }, 4000);
        return () => clearInterval(interval);
    }, [analyzing]);

    // Busy while the request to start is out, and the reason when it could not
    // start. A failed start used to be swallowed, so the click simply did nothing.
    const [startingAnalysis, setStartingAnalysis] = useState(false);
    const [analysisError, setAnalysisError] = useState('');

    const handleRunExtraction = async () => {
        setAnalysisError('');
        setStartingAnalysis(true);
        try {
            const data = await kycPortalService.runExtraction();
            setCaseData(data);
        } catch (err) {
            // Nothing was started -- most often the reading service is not
            // running. The server words it for the partner; nothing is lost.
            setAnalysisError(
                err.response?.data?.error
                || 'Your documents could not be analysed right now. Please try again in a few minutes.',
            );
        } finally {
            setStartingAnalysis(false);
        }
    };

    const saveForm = useCallback(async (data) => {
        if (!dirtyRef.current) return;
        try {
            setSaveState('Saving…');
            await kycPortalService.saveForm(data);
            dirtyRef.current = false;
            setSaveState('All changes saved');
        } catch (err) {
            setSaveState('Could not save — will retry');
        }
    }, []);

    // Debounced autosave while the partner types (only when editable).
    const updateFormData = useCallback((updater) => {
        setFormData((previous) => {
            const next = typeof updater === 'function' ? updater(previous) : updater;
            dirtyRef.current = true;
            setSaveState('Unsaved changes…');
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
            saveTimerRef.current = setTimeout(() => saveForm(next), 1500);
            return next;
        });
    }, [saveForm]);

    useEffect(() => () => {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    }, []);

    const handleStepChange = async (nextStep) => {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        await saveForm(formData);
        setStep(nextStep);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async () => {
        setSubmitError('');
        setSubmitting(true);
        try {
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
            await saveForm(formData);
            const updated = await kycPortalService.submit();
            setCaseData(updated);
        } catch (err) {
            setSubmitError(err.response?.data?.error || 'Submission failed. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleLogout = async () => {
        await kycPortalService.logout();
        history.push('/kyc-portal/login');
    };

    const headerRight = caseData && (
        <>
            <span>
                {caseData.company_name} · KYC {caseData.year}
            </span>
            <button type="button" className="kyc-portal-logout" onClick={handleLogout}>
                Sign Out
            </button>
        </>
    );

    return (
        <KYCPortalShell headerRight={headerRight}>
            <main className="kyc-portal-main">
                {loading ? (
                    <div className="kyc-portal-card">
                        <p className="kyc-portal-help">Loading your KYC case…</p>
                    </div>
                ) : loadError ? (
                    <div className="kyc-portal-card">
                        <div className="kyc-portal-error">{loadError}</div>
                    </div>
                ) : !caseData ? null : (
                    <>
                        {caseData.status === 'submitted' && (
                            <div className="kyc-portal-banner submitted">
                                <strong>Thank you — your KYC pack was submitted on{' '}
                                    {caseData.submitted_at
                                        ? new Date(caseData.submitted_at).toLocaleDateString()
                                        : ''}.</strong>{' '}
                                The IMT compliance team is reviewing it. You will be contacted if
                                anything else is needed.
                            </div>
                        )}
                        {caseData.status === 'approved' && (
                            <div className="kyc-portal-banner approved">
                                <strong>Your KYC onboarding has been approved.</strong>{' '}
                                No further action is required.
                            </div>
                        )}
                        {caseData.status === 'rejected' && (
                            <div className="kyc-portal-banner rejected">
                                <strong>Your KYC submission could not be approved.</strong>{' '}
                                Please contact your IMT representative for details.
                            </div>
                        )}

                        <div className="kyc-portal-steps">
                            {STEPS.map((stepDefinition, index) => (
                                <button
                                    type="button"
                                    key={stepDefinition.key}
                                    className={`kyc-portal-step${index === step ? ' active' : ''}${index < step ? ' done' : ''}`}
                                    onClick={() => handleStepChange(index)}
                                >
                                    <span className="kyc-portal-step-number">{index + 1}</span>
                                    <span>
                                        <div className="kyc-portal-step-title">{stepDefinition.title}</div>
                                        <div className="kyc-portal-step-sub">{stepDefinition.sub}</div>
                                    </span>
                                </button>
                            ))}
                        </div>

                        {step === 0 && (
                            <DocumentsStep
                                caseData={caseData}
                                onCaseUpdate={setCaseData}
                                readOnly={!isEditable}
                                onRunExtraction={handleRunExtraction}
                                analyzing={analyzing || startingAnalysis}
                                analysisError={analysisError}
                            />
                        )}
                        {step === 1 && (
                            <>
                                {analyzing && (
                                    <div className="kyc-portal-banner submitted">
                                        IMT AI is reading your documents — fields will fill in
                                        automatically. You can keep typing; your entries always win.
                                    </div>
                                )}
                                <FormStep
                                    formData={formData}
                                    setFormData={updateFormData}
                                    legacyValues={legacyValues}
                                    readOnly={!isEditable}
                                    sections={getFormSections(caseData.form_type)}
                                    aiInfo={caseData.ai_info}
                                    saveState={saveState}
                                    onBack={() => handleStepChange(0)}
                                    onContinue={() => handleStepChange(2)}
                                />
                            </>
                        )}
                        {step === 2 && (
                            <KYCPortalDeclaration
                                readOnly={!isEditable}
                                onSigned={async () => {
                                    try {
                                        setCaseData(await kycPortalService.getCase());
                                    } catch (err) {
                                        // banner state refreshes on the next poll/navigation
                                    }
                                }}
                            />
                        )}
                        {step === 3 && (
                            isEditable ? (
                                <ReviewStep
                                    caseData={caseData}
                                    formData={formData}
                                    onSubmit={handleSubmit}
                                    submitting={submitting}
                                    submitError={submitError}
                                />
                            ) : (
                                <div className="kyc-portal-card">
                                    <h2>Submission Complete</h2>
                                    <p className="kyc-portal-help">
                                        Your information is with the IMT compliance team. You can
                                        still view your uploads and form entries in the previous steps.
                                    </p>
                                </div>
                            )
                        )}

                        {step !== 1 && (
                            <div className="kyc-portal-actionbar">
                                <div className="kyc-portal-savestate">{isEditable ? saveState : ''}</div>
                                <div style={{ display: 'flex', gap: '0.6rem' }}>
                                    {step > 0 && (
                                        <button
                                            type="button"
                                            className="kyc-portal-btn-secondary"
                                            onClick={() => handleStepChange(step - 1)}
                                        >
                                            ← Back
                                        </button>
                                    )}
                                    {step < STEPS.length - 1 && (
                                        <button
                                            type="button"
                                            className="kyc-portal-btn-primary"
                                            onClick={() => handleStepChange(step + 1)}
                                        >
                                            Continue →
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </main>
        </KYCPortalShell>
    );
};

export default KYCPortalOnboarding;
